import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { blockHas, blockTypeLabel, factDetail, fmtDesc, isTextBlock, type Block, type ComplexFormat, type SetGroup } from '@tg/domain';
import { ICON } from './icons';
import { AiWait } from './AiWait';
import { Svg } from './Svg';
import { AiBtn, LineView } from './Lines';
import { useCtx, isMac, plural } from './ctx';
import * as M from './model';
import { TypePicker } from './TypePicker';
import { Kebab } from './Kebab';

const TEXT_PH = `Пишите как в заметках — блок сохранится текстом, клиент увидит его как есть.
Разобрать на упражнения — кнопка AI.

AMRAP 12:
• трастеры 10 × 40 кг
• подтягивания 8
• гребля 250 м`;
const LIST_KEY = isMac ? '⌘⇧8' : 'Ctrl+Shift+8';
const LIST_RE = /^[•\-–—*][ \t]+/;

/** Поле с локальным значением; в модель — по уходу из поля */
function useLocal(v: string) {
  const [s, set] = useState(v);
  const f = useRef(false);
  useEffect(() => {
    if (!f.current) set(v);
  }, [v]);
  return { s, set, onFocus: () => (f.current = true), onBlurCapture: () => (f.current = false) };
}

/** Заметка к блоку растёт по тексту — длинный комментарий виден целиком, без обрезки */
const grow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
};

export function BlockView({ b, k, quick, pending }: { b: Block; k: number; quick: boolean; pending: boolean }) {
  const c = useCtx();
  const txt = isTextBlock(b);
  const title = useLocal(b.title);
  const note = useLocal(b.note);
  const [noteOpen, setNoteOpen] = useState(false);
  const [ftype, setFtype] = useState<HTMLElement | null>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const showNote = !!b.note || noteOpen;
  const nmw = c.nmw[b.id];

  return (
    <div className={`blk ${txt ? 'tblk' : ''} ${pending ? 'pending' : ''}`} data-blk={b.id} style={nmw ? ({ '--nmw': nmw + 'px' } as React.CSSProperties) : undefined}>
      <div className="blkh">
        <span className="gr" title="Перетащить блок" draggable data-drag-blk={b.id}>
          <Svg src={ICON.grip} />
        </span>
        <input
          className="bt"
          data-f="title"
          value={title.s}
          placeholder={quick ? 'Блок ' + (k + 1) : b.kind || txt ? 'Название блока (необязательно)' : 'Введите название блока'}
          onChange={(e) => title.set(e.target.value)}
          onFocus={title.onFocus}
          onBlurCapture={title.onBlurCapture}
          onBlur={(e) => e.target.value !== b.title && c.update((d) => M.setBlock(d, b.id, { title: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <button className={'ftype ' + (b.kind ? 'on' : '')} data-ftype={b.id} title={b.fmt ? fmtDesc(b.fmt) : b.kind ? '' : 'Разминка, силовая, комплекс…'} onClick={(e) => setFtype(ftype ? null : e.currentTarget)}>
          {b.kind ? blockTypeLabel(b) : 'тип блока'}
        </button>
        <Kebab
          title="Действия с блоком"
          items={[
            {
              k: 'note',
              icon: ICON.chat,
              label: showNote ? 'Удалить заметку' : 'Добавить заметку',
              onClick: () => {
                if (showNote) {
                  note.set('');
                  setNoteOpen(false);
                  if (b.note) c.update((d) => M.setBlock(d, b.id, { note: '' }));
                  return;
                }
                setNoteOpen(true);
                setTimeout(() => noteRef.current?.focus());
              },
            },
            { k: 'save', icon: ICON.star, label: 'Сохранить в базу блоков', onClick: () => c.saveBlockToBase(b.id) },
            !txt &&
              blockHas(b) && {
                k: 'totext',
                icon: ICON.text,
                label: 'Разгруппировать в текстовый блок',
                onClick: () => {
                  const before = c.doc;
                  c.update((d) => M.blockToTextBlock(d, b.id, c.catalog.ex));
                  c.toast('Блок разгруппирован в текст', () => c.update(() => before));
                },
              },
            blockHas(b) && {
              k: 'clear',
              icon: ICON.erase,
              label: 'Очистить блок',
              onClick: () => {
                const before = c.doc;
                c.update((d) => M.clearBlock(d, b.id));
                c.toast('Блок очищен', () => c.update(() => before));
              },
            },
            {
              k: 'del',
              icon: ICON.x,
              label: 'Удалить блок',
              cls: 'rm',
              onClick: () => {
                const before = c.doc;
                c.update((d) => M.removeBlock(d, b.id));
                c.toast('Блок удалён', () => c.update(() => before));
              },
            },
          ]}
        />
      </div>
      {showNote && (
        <label className="bnote">
          <span>
            <Svg src={ICON.chat} />
          </span>
          <textarea
            ref={(el) => {
              noteRef.current = el;
              grow(el);
            }}
            rows={1}
            data-f="note"
            value={note.s}
            placeholder="Заметка к блоку — увидит клиент"
            onChange={(e) => (note.set(e.target.value), grow(e.target))}
            onFocus={note.onFocus}
            onBlurCapture={note.onBlurCapture}
            onBlur={(e) => {
              if (e.target.value !== b.note) c.update((d) => M.setBlock(d, b.id, { note: e.target.value }));
              if (!e.target.value) setNoteOpen(false);
            }}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), e.currentTarget.blur())}
          />
          <button
            className="x"
            data-notedel={b.id}
            title="Удалить заметку"
            onClick={() => {
              note.set('');
              setNoteOpen(false);
              c.update((d) => M.setBlock(d, b.id, { note: '' }));
            }}
          >
            <Svg src={ICON.x} />
          </button>
        </label>
      )}
      {txt ? (
        <TextBody b={b} />
      ) : (
        <>
          {b.items.map((it) => (it.t === 'set' ? <SetView key={it.id} s={it} quick={quick} /> : <LineView key={it.id} it={it} inSet={false} quick={quick} />))}
          <div className="addrow">
            <button
              className="addl"
              data-add={b.id}
              onClick={() => {
                let id = '';
                c.update((d) => {
                  const [n, nid] = M.addLine(d, { block: b.id });
                  id = nid;
                  return n;
                });
                setTimeout(() => c.setFocus(id, 'e'));
              }}
            >
              <Svg src={ICON.plus} /> упражнение
            </button>
          </div>
        </>
      )}
      <BlockResults b={b} />
      {ftype && <TypePicker block={b} anchor={ftype} onChange={(p) => c.update((d) => M.setBlock(d, b.id, p))} onClose={() => setFtype(null)} />}
    </div>
  );
}

function SetView({ s, quick }: { s: SetGroup; quick: boolean }) {
  const c = useCtx();
  const rounds = useLocal(String(s.rounds));
  const rest = useLocal(s.rest || '');
  const r = Math.max(1, +rounds.s || 1);
  return (
    <div className="ssg" data-ss={s.id}>
      <div className="line ssh" data-item={s.id}>
        <span className="gr" title="Перетащить сет" draggable data-drag={s.id}>
          <Svg src={ICON.grip} />
        </span>
        <span className="nm ssb">Сет</span>
        <label className="ssf">
          <input
            className="ssn"
            data-ssf="rounds"
            data-ss-id={s.id}
            value={rounds.s}
            inputMode="numeric"
            maxLength={2}
            onChange={(e) => rounds.set(e.target.value.replace(/\D/g, ''))}
            onFocus={rounds.onFocus}
            onBlurCapture={rounds.onBlurCapture}
            onBlur={() => c.update((d) => M.setSet(d, s.id, { rounds: r }))}
          />
          <s data-ss-rl={s.id}>{plural(r, 'круг', 'круга', 'кругов')}</s>
        </label>
        <label className="ssf">
          <s>отдых</s>
          <input
            className="ssrest"
            data-ssf="rest"
            data-ss-id={s.id}
            value={rest.s}
            placeholder="без отдыха"
            onChange={(e) => rest.set(e.target.value)}
            onFocus={rest.onFocus}
            onBlurCapture={rest.onBlurCapture}
            onBlur={(e) => c.update((d) => M.setSet(d, s.id, { rest: e.target.value.trim() }))}
          />
        </label>
        <span className="sp"></span>
        <button className="x" data-ssungroup={s.id} title="Разгруппировать — упражнения останутся в блоке" onClick={() => c.update((d) => M.ungroup(d, s.id))}>
          <Svg src={ICON.ungroup} />
        </button>
        <button
          className="x"
          data-delss={s.id}
          title="Удалить сет вместе с упражнениями"
          onClick={() => {
            const before = c.doc;
            c.update((d) => M.removeItem(d, s.id));
            c.toast('Сет удалён', () => c.update(() => before));
          }}
        >
          <Svg src={ICON.x} />
        </button>
      </div>
      {s.items.map((it) => (
        <LineView key={it.id} it={it} inSet quick={quick} />
      ))}
      <button
        className="addl in"
        data-addin={s.id}
        onClick={() => {
          let id = '';
          c.update((d) => {
            const [n, nid] = M.addLine(d, { set: s.id });
            id = nid;
            return n;
          });
          setTimeout(() => c.setFocus(id, 'e'));
        }}
      >
        <Svg src={ICON.plus} /> {quick ? 'в сет' : 'упражнение'}
      </button>
    </div>
  );
}

/* Блок текстом (CON-5): лист как в заметках, список хранится текстом с «• » */
function TextBody({ b }: { b: Block }) {
  const c = useCtx();
  const t = useLocal(b.text || '');
  const ta = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [t.s]);
  const commit = (v: string) => v !== b.text && c.update((d) => M.setBlock(d, b.id, { text: v }));

  /* Маркер строкам под курсором */
  const toggleList = () => {
    const el = ta.current;
    if (!el) return;
    const v = el.value;
    const a = v.lastIndexOf('\n', el.selectionStart - 1) + 1;
    let z = v.indexOf('\n', el.selectionEnd);
    if (z < 0) z = v.length;
    const lines = v.slice(a, z).split('\n');
    const on = lines.every((l) => LIST_RE.test(l));
    const out = lines.map((l) => (on ? l.replace(LIST_RE, '') : LIST_RE.test(l) ? l : '• ' + l)).join('\n');
    const nv = v.slice(0, a) + out + v.slice(z);
    t.set(nv);
    commit(nv);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = a + out.length;
    });
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === '8' || e.code === 'Digit8')) {
      e.preventDefault();
      return toggleList();
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      const v = el.value;
      const a = v.lastIndexOf('\n', el.selectionStart - 1) + 1;
      const line = v.slice(a, el.selectionStart);
      const m = line.match(LIST_RE);
      if (!m) return;
      e.preventDefault();
      /* Enter в пустом пункте заканчивает список */
      if (line.trim() === m[0].trim()) {
        const nv = v.slice(0, a) + v.slice(el.selectionStart);
        t.set(nv);
        requestAnimationFrame(() => (el.selectionStart = el.selectionEnd = a));
        return;
      }
      const ins = '\n• ';
      const nv = v.slice(0, el.selectionStart) + ins + v.slice(el.selectionEnd);
      const pos = el.selectionStart + ins.length;
      t.set(nv);
      requestAnimationFrame(() => (el.selectionStart = el.selectionEnd = pos));
    }
  };
  return (
    <div className={'tbody' + (c.aiBusy === b.id ? ' ai-busy' : '')}>
      {c.aiBusy === b.id && <AiWait text="ИИ разбирает тренировку…" />}
      <textarea
        ref={ta}
        className="tbx"
        data-f="text"
        rows={3}
        spellCheck={false}
        placeholder={TEXT_PH}
        value={t.s}
        onChange={(e) => t.set(e.target.value)}
        onFocus={t.onFocus}
        onBlurCapture={t.onBlurCapture}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={onKey}
      />
      <AiBtn attr="aiblk" id={b.id} title="Разобрать текст на упражнения — ИИ предложит, вы проверите" dim={!t.s.trim()} busy={c.aiBusy === b.id} onClick={() => (commit(t.s), c.aiBlock(b.id, t.s))} />
      <div className="tbar">
        <button className="tfmt" data-fmt="ul" tabIndex={-1} title={`Маркированный список · ${LIST_KEY}`} onMouseDown={(e) => e.preventDefault()} onClick={toggleList}>
          <Svg src={ICON.ul} />
          <span>Список</span>
        </button>
        <span className="tkeys">
          <span>
            <kbd>Enter</kbd>новый пункт
          </span>
          <span>
            <kbd>Enter</kbd>
            <kbd>Enter</kbd>конец списка
          </span>
          <span>
            <kbd>{LIST_KEY}</kbd>список
          </span>
        </span>
      </div>
    </div>
  );
}

/** Результат клиента под блоком: итог комплекса и подходы по упражнениям — в порядке строк блока */
function BlockResults({ b }: { b: Block }) {
  const c = useCtx();
  const rs = c.results.filter((r) => r.blockId === b.id);
  if (!rs.length) return null;
  const order: string[] = [];
  for (const y of b.items || []) for (const l of y.t === 'set' ? y.items : [y]) order.push(l.id);
  const rows: { k: string; n: string; t: string }[] = [];
  for (const r of [...rs].sort((x, y) => order.indexOf(x.lineId) - order.indexOf(y.lineId))) {
    const plan = (r.plan || {}) as { sets?: number; text?: string; fmt?: ComplexFormat };
    const fact = (r.fact || {}) as Record<string, unknown>;
    const t = r.lineId ? factDetail(plan, fact) : factDetail(null, fact, plan.fmt ?? b.fmt ?? null);
    if (t) rows.push({ k: r.lineId || 'blk', n: r.lineId ? (r.exId && c.catalog.ex(r.exId)?.ru) || plan.text || 'Упражнение' : 'Итог', t });
  }
  if (!rows.length) return null;
  return (
    <div className="blkres">
      <span className="k">
        <Svg src={ICON.chk} /> Результат клиента
      </span>
      {rows.map((r) => (
        <span key={r.k} className="r">
          <b>{r.n}</b> {r.t}
        </span>
      ))}
    </div>
  );
}
